// Bảng tin đọc từ API: trang đầu khi đổi bộ lọc / từ khoá, "Xem thêm" bằng con trỏ, người đang đăng nhập, số đỏ.
import { useCallback, useEffect, useRef, useState } from 'react';
import { message } from 'antd';
import { feedApi, SocialApiError, type LeaderRow } from './socialApi';
import { currentUser, type FeedFilter, type Me, type Post } from './socialStore';

export function useFeed(filter: FeedFilter, q: string) {
  const [posts, setPosts] = useState<Post[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const next = useRef<string | null>(null);
  // bỏ kết quả cũ khi người dùng gõ / đổi bộ lọc nhanh
  const reqId = useRef(0);

  const load = useCallback(async (reset: boolean) => {
    const id = ++reqId.current;
    setLoading(true);
    if (reset) setError(null);
    try {
      const page = await feedApi.list({ filter, q, cursor: reset ? null : next.current });
      if (id !== reqId.current) return;
      setPosts(prev => (reset ? page.posts : [...prev, ...page.posts.filter(p => !prev.some(x => x.id === p.id))]));
      next.current = page.nextCursor;
      setHasMore(page.hasMore && !!page.nextCursor);
    } catch (e: any) {
      if (id !== reqId.current) return;
      if (reset) { setPosts([]); setHasMore(false); setError(e.message); } else message.error(e.message);
    } finally {
      if (id === reqId.current) setLoading(false);
    }
  }, [filter, q]);

  // Đổi bộ lọc / từ khoá: bỏ danh sách cũ ngay, không để bài của bộ lọc trước hiện dưới tên bộ lọc mới trong lúc chờ
  useEffect(() => {
    setPosts([]);
    setHasMore(false);
    next.current = null;
    load(true);
  }, [load]);

  const patch = useCallback((id: string, fn: (p: Post) => Post) => setPosts(ps => ps.map(p => (p.id === id ? fn(p) : p))), []);
  const prepend = useCallback((p: Post) => setPosts(ps => [p, ...ps.filter(x => x.id !== p.id)]), []);
  const remove = useCallback((id: string) => setPosts(ps => ps.filter(p => p.id !== id)), []);

  return { posts, hasMore, loading, error, loadMore: () => load(false), reload: () => load(true), patch, prepend, remove };
}

/** Người đang đăng nhập theo GET /me; trong lúc chờ dùng tạm thông tin đăng nhập CMS đã lưu */
export function useSocialMe() {
  const [me, setMe] = useState<Me>(currentUser);
  useEffect(() => {
    feedApi.me().then(setMe).catch(e => {
      if (e instanceof SocialApiError && e.status === 403) message.warning(`${e.message} — bạn không dùng được bảng tin.`);
    });
  }, []);
  return me;
}

export function useCounters() {
  const [counts, setCounts] = useState({ mentions: 0, scheduled: 0 });
  const refresh = useCallback(() => { feedApi.counters().then(setCounts).catch(() => { /* giữ số cũ */ }); }, []);
  useEffect(() => { refresh(); }, [refresh]);
  return { ...counts, refresh };
}

/** Khối "Ghi nhận mới nhất" và bảng xếp hạng: API tính, tải lại sau khi gửi / sửa / xoá ghi nhận */
export function useRecognitions() {
  const [latest, setLatest] = useState<Post | null>(null);
  const [board, setBoard] = useState<LeaderRow[]>([]);
  const reload = useCallback(() => {
    feedApi.latestRecognition().then(setLatest).catch(() => { /* giữ dữ liệu cũ */ });
    feedApi.leaderboard().then(setBoard).catch(() => { /* giữ dữ liệu cũ */ });
  }, []);
  useEffect(() => { reload(); }, [reload]);
  return { latest, board, reload };
}

/** Bài hẹn giờ của tôi (sắp tới trước) */
export function useScheduled() {
  const [posts, setPosts] = useState<Post[]>([]);
  const reload = useCallback(() => { feedApi.scheduled().then(setPosts).catch(() => { /* giữ danh sách cũ */ }); }, []);
  useEffect(() => { reload(); }, [reload]);
  const remove = useCallback((id: string) => setPosts(ps => ps.filter(p => p.id !== id)), []);
  return { posts, reload, remove };
}
